'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import ServiceCard from './ServiceCard';
import ServiceDetailModal from './ServiceDetailModal';
import ComparisonMatrix from './ComparisonMatrix';
import ProcessTimeline from './ProcessTimeline';
import IndustryPackages from './IndustryPackages';
import AssessmentTool from './AssessmentTool';
import Icon from '@/components/ui/AppIcon';

interface Service {
  icon: string;
  title: string;
  description: string;
  features: string[];
  benefits: string[];
  process: string[];
  image: string;
  alt: string;
}

interface MatrixFeature {
  name: string;
  basic: boolean;
  professional: boolean;
  enterprise: boolean;
}

interface TimelineStep {
  phase: string;
  duration: string;
  activities: string[];
}

interface IndustryPackage {
  industry: string;
  icon: string;
  description: string;
  services: string[];
  image: string;
  alt: string;
  caseStudyCount: number;
}

interface Question {
  id: number;
  question: string;
  options: string[];
}

const ServicesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
  {
    icon: 'ChartBarIcon',
    title: 'Marketing Strategy & Automation',
    description: 'Transform your marketing operations with intelligent automation and data-driven strategies that deliver measurable results.',
    features: [
    'Multi-channel campaign automation',
    'Customer journey mapping and optimization',
    'Advanced analytics and reporting dashboards',
    'Lead scoring and nurturing workflows',
    'A/B testing and conversion optimization'],

    benefits: [
    'Increase marketing efficiency by 60%',
    'Reduce customer acquisition costs by 40%',
    'Improve lead quality and conversion rates',
    'Scale campaigns without proportional resource increase',
    'Gain real-time visibility into marketing performance'],

    process: [
    'Discovery & Audit: Comprehensive analysis of current marketing operations and technology stack',
    'Strategy Development: Custom roadmap aligned with business objectives and growth targets',
    'Platform Selection: Evaluation and recommendation of optimal automation tools',
    'Implementation: Phased rollout with training and change management support',
    'Optimization: Continuous monitoring, testing, and refinement for optimal performance'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15e8ee5dd-1770492432745.png",
    alt: 'Modern office workspace with multiple computer monitors displaying colorful marketing analytics dashboards and campaign performance metrics'
  },
  {
    icon: 'CpuChipIcon',
    title: 'AI Implementation & Integration',
    description: 'Harness the power of artificial intelligence to automate complex processes and unlock new capabilities across your organization.',
    features: [
    'Custom AI model development and training',
    'Natural language processing solutions',
    'Predictive analytics and forecasting',
    'Computer vision applications',
    'Seamless integration with existing systems'],

    benefits: [
    'Automate repetitive tasks saving 1000+ hours annually',
    'Improve decision accuracy with predictive insights',
    'Enhance customer experience through personalization',
    'Reduce operational costs by 35%',
    'Gain competitive advantage through innovation'],

    process: [
    'Use Case Identification: Workshop to identify high-impact AI opportunities',
    'Feasibility Analysis: Technical assessment and viability evaluation',
    'Proof of Concept: Rapid prototype to validate approach',
    'Full Development: Production-ready AI solution with testing',
    'Deployment & Support: Launch with ongoing monitoring and optimization'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_113c8f6f0-1764648553585.png",
    alt: 'Futuristic digital interface showing artificial intelligence neural network visualization with glowing blue nodes and connections'
  },
  {
    icon: 'Cog6ToothIcon',
    title: 'Business Process Optimization',
    description: 'Streamline operations and eliminate inefficiencies through systematic process analysis and intelligent automation solutions.',
    features: [
    'End-to-end process mapping and analysis',
    'Bottleneck identification and resolution',
    'Workflow automation design',
    'Integration architecture planning',
    'Change management and training'],

    benefits: [
    'Reduce process cycle times by 50%',
    'Eliminate manual errors and rework',
    'Improve employee productivity and satisfaction',
    'Enhance compliance and audit readiness',
    'Create scalable operational foundation'],

    process: [
    'Process Discovery: Document current state workflows and pain points',
    'Analysis & Design: Identify optimization opportunities and design future state',
    'Solution Architecture: Plan technology stack and integration approach',
    'Implementation: Deploy automation with phased rollout',
    'Continuous Improvement: Monitor metrics and iterate for ongoing gains'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c2e67d4f-1767095336985.png",
    alt: 'Business professionals collaborating around conference table with flowchart diagrams and process optimization documents'
  },
  {
    icon: 'MapIcon',
    title: 'Digital Transformation Roadmaps',
    description: 'Navigate your digital evolution with confidence through comprehensive strategic planning and phased implementation guidance.',
    features: [
    'Current state assessment and gap analysis',
    'Technology stack evaluation',
    'Prioritized initiative roadmap',
    'Budget and resource planning',
    'Risk mitigation strategies'],

    benefits: [
    'Clear vision and alignment across organization',
    'Reduced technology investment risk',
    'Faster time to value from initiatives',
    'Improved stakeholder buy-in',
    'Measurable progress tracking'],

    process: [
    'Assessment: Evaluate current capabilities and digital maturity',
    'Vision Setting: Define target state and success metrics',
    'Roadmap Creation: Develop phased plan with dependencies',
    'Stakeholder Alignment: Present and refine with leadership',
    'Execution Support: Provide ongoing guidance during implementation'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_107e97693-1767000018225.png",
    alt: 'Strategic planning session with digital transformation roadmap timeline displayed on large screen showing phases and milestones'
  },
  {
    icon: 'WrenchScrewdriverIcon',
    title: 'Custom Solution Development',
    description: 'Build bespoke technology solutions tailored to your unique business requirements and competitive positioning.',
    features: [
    'Requirements gathering and specification',
    'Custom application development',
    'API development and integration',
    'Cloud infrastructure setup',
    'Security and compliance implementation'],

    benefits: [
    'Perfect fit for unique business needs',
    'Competitive differentiation through proprietary tools',
    'Full ownership and control of solution',
    'Scalable architecture for future growth',
    'Ongoing support and enhancement'],

    process: [
    'Discovery: Deep dive into requirements and constraints',
    'Design: Create technical architecture and user experience',
    'Development: Agile sprints with regular demos',
    'Testing: Comprehensive QA and user acceptance testing',
    'Launch: Deployment with training and documentation'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfbc27cd-1766500363073.png",
    alt: 'Software developer working on custom application code on multiple monitors in modern tech office environment'
  }];


  const comparisonFeatures: MatrixFeature[] = [
  { name: 'Strategy Consultation', basic: true, professional: true, enterprise: true },
  { name: 'Implementation Support', basic: true, professional: true, enterprise: true },
  { name: 'Monthly Reporting', basic: true, professional: true, enterprise: true },
  { name: 'Dedicated Account Manager', basic: false, professional: true, enterprise: true },
  { name: 'Priority Support (24/7)', basic: false, professional: true, enterprise: true },
  { name: 'Custom Integration Development', basic: false, professional: false, enterprise: true },
  { name: 'Advanced Analytics Dashboard', basic: false, professional: true, enterprise: true },
  { name: 'Quarterly Strategy Reviews', basic: false, professional: true, enterprise: true },
  { name: 'White-label Solutions', basic: false, professional: false, enterprise: true },
  { name: 'API Access', basic: false, professional: true, enterprise: true }];


  const timelineSteps: TimelineStep[] = [
  {
    phase: 'Discovery & Assessment',
    duration: '1-2 weeks',
    activities: [
    'Initial consultation and goal setting',
    'Current state analysis and documentation',
    'Stakeholder interviews and requirements gathering',
    'Technology stack evaluation']

  },
  {
    phase: 'Strategy & Planning',
    duration: '2-3 weeks',
    activities: [
    'Solution architecture design',
    'Implementation roadmap creation',
    'Resource allocation and timeline planning',
    'Risk assessment and mitigation planning']

  },
  {
    phase: 'Implementation',
    duration: '4-8 weeks',
    activities: [
    'Phased rollout of solutions',
    'Integration with existing systems',
    'Team training and change management',
    'Quality assurance and testing']

  },
  {
    phase: 'Optimization & Support',
    duration: 'Ongoing',
    activities: [
    'Performance monitoring and reporting',
    'Continuous improvement initiatives',
    'Regular strategy reviews',
    'Technical support and maintenance']

  }];


  const industryPackages: IndustryPackage[] = [
  {
    industry: 'E-commerce',
    icon: 'ShoppingCartIcon',
    description: 'Comprehensive solutions for online retailers looking to scale operations, improve conversion rates, and deliver exceptional customer experiences.',
    services: [
    'Conversion rate optimization and A/B testing',
    'Personalized product recommendation engines',
    'Inventory management automation',
    'Customer lifecycle marketing automation',
    'Multi-channel sales integration'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_122b22f9d-1764654516001.png",
    alt: 'Modern e-commerce warehouse with organized product shelves and digital inventory management system displays',
    caseStudyCount: 12
  },
  {
    industry: 'Healthcare',
    icon: 'HeartIcon',
    description: 'HIPAA-compliant solutions that improve patient outcomes, streamline operations, and enhance the healthcare delivery experience.',
    services: [
    'Patient engagement and communication automation',
    'Appointment scheduling optimization',
    'Medical records management systems',
    'Telehealth platform integration',
    'Compliance and security implementation'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_195a6e902-1764648555298.png",
    alt: 'Healthcare professional using digital tablet with patient management software in modern medical facility',
    caseStudyCount: 8
  },
  {
    industry: 'Finance',
    icon: 'BanknotesIcon',
    description: 'Secure, compliant solutions that automate financial processes, enhance risk management, and improve customer service.',
    services: [
    'Automated compliance reporting',
    'Fraud detection and prevention systems',
    'Customer onboarding automation',
    'Portfolio management tools',
    'Regulatory technology implementation'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe26f223-1764664593903.png",
    alt: 'Financial analyst reviewing investment portfolio data on multiple screens showing market trends and analytics',
    caseStudyCount: 15
  },
  {
    industry: 'Manufacturing',
    icon: 'CubeIcon',
    description: 'Industry 4.0 solutions that optimize production, reduce waste, and enable predictive maintenance for maximum efficiency.',
    services: [
    'Production line automation and monitoring',
    'Predictive maintenance systems',
    'Supply chain optimization',
    'Quality control automation',
    'IoT sensor integration'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1048e0d49-1768497938824.png",
    alt: 'Automated manufacturing facility with robotic arms and conveyor systems in modern industrial setting',
    caseStudyCount: 10
  },
  {
    industry: 'Real Estate',
    icon: 'HomeModernIcon',
    description: 'Technology solutions that streamline property management, enhance client experiences, and automate transaction processes.',
    services: [
    'Property management automation',
    'Virtual tour and 3D visualization',
    'Lead generation and nurturing',
    'Document management and e-signatures',
    'Market analysis and pricing tools'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_179028f1b-1768432415134.png",
    alt: 'Modern luxury residential property exterior with professional real estate photography and architectural details',
    caseStudyCount: 9
  },
  {
    industry: 'Education',
    icon: 'AcademicCapIcon',
    description: 'EdTech solutions that enhance learning outcomes, streamline administration, and create engaging educational experiences.',
    services: [
    'Learning management system integration',
    'Student engagement analytics',
    'Administrative process automation',
    'Virtual classroom technology',
    'Assessment and grading automation'],

    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e56c0a8a-1766825006595.png",
    alt: 'Modern classroom with students using tablets and interactive digital learning technology',
    caseStudyCount: 7
  }];


  const assessmentQuestions: Question[] = [
  {
    id: 1,
    question: 'How would you describe your current marketing automation capabilities?',
    options: [
    'No automation - everything is manual',
    'Basic email automation only',
    'Multiple tools with some integration',
    'Comprehensive automation across channels']

  },
  {
    id: 2,
    question: 'What is your primary business challenge right now?',
    options: [
    'Generating more qualified leads',
    'Improving operational efficiency',
    'Scaling without increasing costs',
    'Staying competitive with technology']

  },
  {
    id: 3,
    question: 'How do you currently measure marketing performance?',
    options: [
    'We don\'t track it systematically',
    'Basic metrics like website traffic',
    'Attribution across some channels',
    'Full funnel attribution and analytics']

  },
  {
    id: 4,
    question: 'What is your organization\'s attitude toward AI and automation?',
    options: [
    'Skeptical - prefer traditional methods',
    'Curious but uncertain where to start',
    'Ready to implement with guidance',
    'Already using AI in some areas']

  },
  {
    id: 5,
    question: 'How would you rate your team\'s technical capabilities?',
    options: [
    'Limited - need significant support',
    'Basic - can handle simple tools',
    'Intermediate - comfortable with technology',
    'Advanced - have in-house developers']

  }];


  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="h-32 bg-muted/50 rounded animate-pulse mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) =>
            <div key={i} className="h-96 bg-muted/50 rounded animate-pulse"></div>
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-secondary to-primary/80 text-primary-foreground pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6">
              Transform Your Business with Strategic Solutions
            </h1>
            <p className="text-xl text-primary-foreground/90 mb-8">
              Comprehensive services that bridge the gap between traditional operations and cutting-edge AI automation
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setActiveTab('assessment')}
                className="px-8 py-3 bg-primary-foreground text-primary rounded-md font-semibold hover:bg-primary-foreground/90 transition-all duration-300 ease-brand">
                Take Assessment
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-card border-b border-border sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center space-x-1 overflow-x-auto py-4">
            {[
            { id: 'overview', label: 'Overview', icon: 'ViewColumnsIcon' },
            { id: 'comparison', label: 'Compare Plans', icon: 'TableCellsIcon' },
            { id: 'process', label: 'Our Process', icon: 'ClipboardDocumentListIcon' },
            { id: 'industries', label: 'Industries', icon: 'BuildingOfficeIcon' },
            { id: 'assessment', label: 'Assessment', icon: 'ClipboardDocumentCheckIcon' }].
            map((tab) =>
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-300 ease-brand ${
              activeTab === tab.id ?
              'bg-primary text-primary-foreground' :
              'text-foreground hover:bg-muted'}`
              }>

                <Icon name={tab.icon as any} size={16} />
                <span>{tab.label}</span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      {activeTab === 'overview' &&
      <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
                Our Services
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Comprehensive solutions designed to accelerate your digital transformation journey
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) =>
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              features={service.features.slice(0, 3)}
              onLearnMore={() => setSelectedService(service)} />

            )}
            </div>
          </div>
        </section>
      }

      {/* Comparison Matrix */}
      {activeTab === 'comparison' &&
      <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
                Compare Service Tiers
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Choose the right level of support for your business needs. All pricing is customized based on your specific requirements.
              </p>
            </div>

            <div className="bg-card rounded-lg shadow-subtle border border-border p-6">
              <ComparisonMatrix features={comparisonFeatures} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-card rounded-lg p-6 shadow-subtle border border-border">
                <h3 className="text-xl font-display font-bold text-foreground mb-2">Basic</h3>
                <p className="text-3xl font-display font-bold text-primary mb-4">Quote-Based</p>
                <p className="text-sm text-muted-foreground mb-4">Perfect for growing businesses starting their digital transformation. Contact us for a customized quote.</p>
              </div>

              <div className="bg-card rounded-lg p-6 shadow-elevation border-2 border-primary relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-accent text-accent-foreground rounded-full text-xs font-bold">
                  MOST POPULAR
                </div>
                <h3 className="text-xl font-display font-bold text-foreground mb-2">Professional</h3>
                <p className="text-3xl font-display font-bold text-primary mb-4">Quote-Based</p>
                <p className="text-sm text-muted-foreground mb-4">Comprehensive solutions for established businesses scaling operations. Contact us for a customized quote.</p>
              </div>

              <div className="bg-card rounded-lg p-6 shadow-subtle border border-border">
                <h3 className="text-xl font-display font-bold text-foreground mb-2">Enterprise</h3>
                <p className="text-3xl font-display font-bold text-primary mb-4">Quote-Based</p>
                <p className="text-sm text-muted-foreground mb-4">Tailored solutions for large organizations with complex needs. Contact us for a customized quote.</p>
              </div>
            </div>
          </div>
        </section>
      }

      {/* Process Timeline */}
      {activeTab === 'process' &&
      <section className="py-16 bg-background">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
                Our Implementation Process
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A structured methodology that ensures successful delivery and quality results
              </p>
            </div>

            <ProcessTimeline steps={timelineSteps} />
          </div>
        </section>
      }

      {/* Industry Packages */}
      {activeTab === 'industries' &&
      <section className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
                Industry-Specific Solutions
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Tailored service packages designed for your industry's unique challenges
              </p>
            </div>

            <IndustryPackages packages={industryPackages} />
          </div>
        </section>
      }

      {/* Assessment Tool */}
      {activeTab === 'assessment' &&
      <section className="py-16 bg-background">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
                Business Readiness Assessment
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Discover which services are right for your current stage of digital maturity
              </p>
            </div>

            <AssessmentTool questions={assessmentQuestions} />
          </div>
        </section>
      }

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary via-secondary to-primary/80 text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-primary-foreground/90 mb-8">
            Contact us to discuss your specific needs and get a customized quote
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/portfolio"
              className="px-8 py-3 bg-primary-foreground text-primary rounded-md font-semibold hover:bg-primary-foreground/90 transition-all duration-300 ease-brand">
              View Case Studies
            </Link>
          </div>
        </div>
      </section>

      {/* Service Detail Modal */}
      {selectedService &&
      <ServiceDetailModal
        service={selectedService}
        onClose={() => setSelectedService(null)} />

      }
    </>);

};

export default ServicesInteractive;